import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { 
  Download, 
  Database, 
  FileJson, 
  RefreshCw,
  Users,
  Building2,
  Briefcase,
  FolderOpen,
  Calendar,
  MessageSquare,
  Bell,
  Heart,
  FileText,
  Star,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const collectionIcons = {
  users: Users,
  companies: Building2,
  projects: Briefcase,
  resources: FolderOpen,
  pre_reservations: Calendar,
  collaborations: Calendar,
  messages: MessageSquare,
  notifications: Bell,
  favorites: Heart,
  contract_templates: FileText,
  contracts: FileText,
  ratings: Star,
  tickets: MessageSquare
};

const DataExportPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const { toast } = useToast();

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/database-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const exportCollection = async (collectionName) => {
    try {
      setExporting(collectionName);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/export-data/${collectionName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collectionName}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export réussi",
        description: `${response.data.count} documents exportés de ${collectionName}`,
      });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({
        title: "Erreur d'export",
        description: error.response?.data?.detail || "Impossible d'exporter les données",
        variant: "destructive"
      });
    } finally {
      setExporting(null);
    }
  };

  const exportAllData = async () => {
    try {
      setExportingAll(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/export-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snow_exchange_full_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export complet réussi",
        description: "Toutes les données ont été exportées",
      });
    } catch (error) {
      console.error('Error exporting all:', error);
      toast({
        title: "Erreur d'export",
        description: error.response?.data?.detail || "Impossible d'exporter toutes les données",
        variant: "destructive"
      });
    } finally {
      setExportingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Export des Données</h1>
          <p className="text-muted-foreground">
            Exportez les données de la base de données en JSON
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchStats}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={exportAllData} disabled={exportingAll}>
            {exportingAll ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter Tout
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Statistiques de la Base de Données
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">
                {stats?.total_collections || 0}
              </div>
              <div className="text-sm text-muted-foreground">Collections</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">
                {stats?.total_documents?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-muted-foreground">Documents Total</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">
                {stats?.collections?.users || 0}
              </div>
              <div className="text-sm text-muted-foreground">Utilisateurs</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">
                {stats?.collections?.companies || 0}
              </div>
              <div className="text-sm text-muted-foreground">Entreprises</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Collections Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Collections Disponibles</CardTitle>
          <CardDescription>
            Cliquez sur une collection pour l'exporter en JSON
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats?.collections && Object.entries(stats.collections)
              .sort((a, b) => b[1] - a[1])
              .map(([name, count]) => {
                const Icon = collectionIcons[name] || FileJson;
                const isExporting = exporting === name;
                
                return (
                  <div 
                    key={name}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{name}</div>
                        <div className="text-sm text-muted-foreground">
                          {count.toLocaleString()} documents
                        </div>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => exportCollection(name)}
                      disabled={isExporting || count === 0}
                    >
                      {isExporting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Export Info */}
      <Card>
        <CardHeader>
          <CardTitle>Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• Les mots de passe des utilisateurs sont masqués dans les exports</p>
          <p>• Les fichiers sont exportés au format JSON</p>
          <p>• L'export complet inclut toutes les collections principales</p>
          <p>• Maximum 10,000 documents par collection</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataExportPage;
